/**
 * Activity sidebar layout.
 *
 * Threads are the spine here. One recency-ordered list — pinned held on top —
 * with the project picker acting as a filter over it rather than as a container.
 *
 * Chrome follows t3code's sidebar: one full-width scope trigger carrying the
 * scoped project's own icon, a picker that lists every project with its icon
 * and a settings affordance, and the new-project control sitting beside the
 * trigger as a peer icon button. "New thread" already lives at the top of the
 * sidebar (`SearchAndCommandCenter`), so this header does not repeat it.
 *
 * The picker's rows share the trigger's left edge — same icon column, same
 * label column — which is why its items drop the menu's radio-indicator
 * column (`hideIndicator`) and mark the current scope with a filled row
 * instead.
 *
 * Deliberately carries no runtime chrome: no environment groups, no runtime
 * status dots, no start/stop controls. Those belong to the Projects layout and
 * to Settings → Connections. Activity answers one question — what am I working
 * on — and anything that does not serve it is removed rather than shrunk.
 */

import { useState, type ReactNode } from "react";
import { ChevronDownIcon, FolderIcon, FolderPlusIcon, SettingsIcon } from "lucide-react";

import { ProjectIcon } from "../ProjectIcon";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import { SidebarFixedHeader, SidebarGroup, SidebarMenuButton } from "../ui/sidebar";
import { Button } from "../ui/button";
import { Menu, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuTrigger } from "../ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "../../lib/utils";
import {
  ACTIVITY_SECTION_ID,
  type ActivityScopeProject,
  type ActivityThreadSections,
} from "./activitySidebar.logic";
import type { SidebarThreadSummary } from "~/types";

export const ACTIVITY_ALL_PROJECTS_LABEL = "All projects";

/**
 * Shared by every row in the picker so the "All projects" entry, each project,
 * and their settings buttons sit on one icon column and one label column. Base
 * UI puts the item's children in its last span, so the layout has to be
 * applied there.
 */
const PICKER_ITEM_CLASS =
  "h-8 min-h-8 px-1 py-0 text-sm font-medium [&>span:last-child]:flex [&>span:last-child]:min-w-0 [&>span:last-child]:items-center [&>span:last-child]:gap-2";

export interface ActivitySidebarProps {
  readonly navigationChrome: ReactNode;
  readonly projects: readonly ActivityScopeProject[];
  readonly sections: ActivityThreadSections<SidebarThreadSummary>;
  readonly scopeKey: string | null;
  readonly scopeLabel: string;
  readonly onScopeKeyChange: (scopeKey: string | null) => void;
  readonly archivedShelfExpanded: boolean;
  readonly onToggleArchivedShelf: () => void;
  readonly emptyStateLabel: string;
  readonly hasProjects: boolean;
  readonly onAddProject: () => void;
  readonly onProjectSettings: (project: ActivityScopeProject) => void;
  /**
   * Renders one flat list of thread rows. Supplied by `ActiveThreadsSection`
   * so Activity and Projects render byte-identical rows — same context menus,
   * same rename flow, same worker sub-rows — instead of drifting into two
   * implementations of the same row.
   */
  readonly renderThreadList: (
    sectionId: string,
    threads: readonly SidebarThreadSummary[],
    orderedThreadKeys?: readonly string[],
  ) => ReactNode;
}

function SectionHeader({ label, count }: { readonly label: string; readonly count: number }) {
  return (
    <div className="mb-1 flex items-center gap-2 px-2">
      <span className="text-[11px] font-medium tracking-[0.08em] text-muted-foreground/50 uppercase">
        {label}
      </span>
      <span className="text-[10px] text-muted-foreground/40">{count}</span>
    </div>
  );
}

/**
 * The scoped project's own icon, falling back to a folder for "All projects".
 * Delegates to `ProjectIcon` so the picker resolves identity exactly as
 * the Projects layout's rows do: a CMMD-canonical icon when the project has
 * one, the repo favicon otherwise. Cloud projects carry no cwd, so without the
 * canonical branch every one of them rendered the same letter avatar.
 */
function ScopeGlyph({ project }: { readonly project: ActivityScopeProject | null }) {
  if (!project) return <FolderIcon className="size-4 shrink-0" />;
  return <ProjectGlyph project={project} />;
}

function ProjectGlyph({ project }: { readonly project: ActivityScopeProject }) {
  return (
    <ProjectIcon
      environmentId={project.environmentId as EnvironmentId}
      cwd={project.cwd}
      projectName={project.name}
      canonicalIdentity={project.canonicalIdentity}
      className="size-4 shrink-0"
    />
  );
}

/**
 * Scope trigger, new-thread, and new-project share one row and one chrome, so
 * the picker reads as the row's subject and the two icon buttons as its peers.
 */
function ActivityHeader(props: {
  readonly projects: readonly ActivityScopeProject[];
  readonly scopeKey: string | null;
  readonly scopeLabel: string;
  readonly onScopeKeyChange: (scopeKey: string | null) => void;
  readonly hasProjects: boolean;
  readonly onAddProject: () => void;
  readonly onProjectSettings: (project: ActivityScopeProject) => void;
}) {
  const scopedProject =
    props.projects.find((project) => project.projectKey === props.scopeKey) ?? null;
  // Controlled so the gear can close the picker. Choosing a scope closes it on
  // its own (`closeOnClick`), but the gear stops the press from reaching the
  // radio item — otherwise it would re-scope the sidebar on its way to
  // settings — and that stops the menu's own close with it, leaving the popup
  // on top of the page the gear just opened.
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="flex items-center gap-1">
      {props.hasProjects ? (
        <Menu open={pickerOpen} onOpenChange={setPickerOpen}>
          <MenuTrigger
            render={
              <SidebarMenuButton
                aria-label="Filter threads by project"
                // The popup is anchored to this button and inset by its own
                // 1px border, so the trigger gives that pixel back — otherwise
                // the open picker sits one pixel right of its own label.
                className="min-w-0 flex-1 ps-[calc(var(--sidebar-row-content-inset)-1px)] focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar"
              />
            }
          >
            <ScopeGlyph project={scopedProject} />
            <span className="min-w-0 flex-1 truncate text-left">{props.scopeLabel}</span>
            <ChevronDownIcon className="-mr-px size-4 shrink-0" />
          </MenuTrigger>
          <MenuPopup align="start" className="max-h-80 w-(--anchor-width) overflow-y-auto">
            <MenuRadioGroup
              value={props.scopeKey ?? "__all__"}
              onValueChange={(value) =>
                props.onScopeKeyChange(value === "__all__" ? null : (value as string))
              }
            >
              <MenuRadioItem
                value="__all__"
                closeOnClick
                hideIndicator
                className={PICKER_ITEM_CLASS}
              >
                <FolderIcon className="size-4 shrink-0" />
                <span className="min-w-0 truncate text-sm">{ACTIVITY_ALL_PROJECTS_LABEL}</span>
              </MenuRadioItem>
              {props.projects.map((project) => (
                <MenuRadioItem
                  key={project.projectKey}
                  value={project.projectKey}
                  closeOnClick
                  hideIndicator
                  className={PICKER_ITEM_CLASS}
                >
                  <ProjectGlyph project={project} />
                  <span className="min-w-0 truncate text-sm">{project.name}</span>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    // No `title`: Forge bans native tooltips
                    // (forge/no-native-title-tooltip). The label carries the
                    // destination for assistive tech, and a Tooltip popup
                    // inside an open menu would stack two layers.
                    aria-label={`Project settings for ${project.name}`}
                    className="ml-auto size-6 shrink-0 text-muted-foreground/70 hover:text-foreground focus-visible:bg-accent focus-visible:text-foreground"
                    // Stop the radio item from claiming the press, or the gear
                    // would also change the scope on its way to settings.
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setPickerOpen(false);
                      props.onProjectSettings(project);
                    }}
                  >
                    <SettingsIcon className="size-3.5" />
                  </Button>
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuPopup>
        </Menu>
      ) : (
        <span className="min-w-0 flex-1 truncate px-2 text-[11px] font-medium tracking-[0.08em] text-muted-foreground/50 uppercase">
          Activity
        </span>
      )}
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarMenuButton
              size="sm"
              type="button"
              aria-label="New project"
              onClick={props.onAddProject}
              className="size-8 shrink-0 justify-center p-0 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar"
            />
          }
        >
          <FolderPlusIcon className="size-4" />
        </TooltipTrigger>
        <TooltipPopup side="right">New project</TooltipPopup>
      </Tooltip>
    </div>
  );
}

// fallow-ignore-next-line complexity -- the three lifecycle shelves share one layout boundary.
export function ActivitySidebar(props: ActivitySidebarProps) {
  const { pinned, active } = props.sections;
  const settled = props.sections.settled ?? [];
  const isEmpty = pinned.length === 0 && active.length === 0 && settled.length === 0;

  return (
    <SidebarGroup className="gap-1 px-2 pt-2 pb-1">
      <SidebarFixedHeader>
        <div className="flex flex-col gap-1 px-2 pt-2">
          <ActivityHeader
            projects={props.projects}
            scopeKey={props.scopeKey}
            scopeLabel={props.scopeLabel}
            onScopeKeyChange={props.onScopeKeyChange}
            hasProjects={props.hasProjects}
            onAddProject={props.onAddProject}
            onProjectSettings={props.onProjectSettings}
          />
          <div data-testid="project-navigation-chrome">{props.navigationChrome}</div>
        </div>
      </SidebarFixedHeader>

      {pinned.length > 0 ? (
        <div data-testid="activity-sidebar-pinned">
          <SectionHeader label="Pinned" count={pinned.length} />
          {props.renderThreadList(ACTIVITY_SECTION_ID.pinned, pinned)}
          <div aria-hidden className="mx-2.5 my-1.5 h-px bg-sidebar-border/60" />
        </div>
      ) : null}

      {active.length > 0 ? (
        <div data-testid="activity-sidebar-active">
          {props.renderThreadList(ACTIVITY_SECTION_ID.active, active)}
        </div>
      ) : null}

      {settled.length > 0 ? (
        <div data-testid="activity-sidebar-settled">
          <button
            type="button"
            onClick={props.onToggleArchivedShelf}
            aria-expanded={props.archivedShelfExpanded}
            data-testid="activity-sidebar-settled-toggle"
            className="mt-3 mb-1 flex w-full cursor-pointer items-center gap-2 px-2.5 text-left"
          >
            <span className="text-xs font-medium text-muted-foreground/50">
              {props.archivedShelfExpanded ? "Settled" : `Settled (${settled.length})`}
            </span>
            <span aria-hidden className="h-px flex-1 bg-sidebar-border/60" />
            <ChevronDownIcon
              aria-hidden
              className={cn(
                "size-3 text-muted-foreground/50 transition-transform",
                props.archivedShelfExpanded && "rotate-180",
              )}
            />
          </button>
          {props.archivedShelfExpanded
            ? props.renderThreadList(ACTIVITY_SECTION_ID.settled, settled)
            : null}
        </div>
      ) : null}

      {isEmpty ? (
        <div className="flex flex-col items-center gap-2 px-2 py-6 text-center text-xs text-muted-foreground/60">
          <span>{props.emptyStateLabel}</span>
          {props.hasProjects ? null : (
            <button
              type="button"
              onClick={props.onAddProject}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <FolderPlusIcon className="-mx-0.5 size-3" />
              Add project
            </button>
          )}
        </div>
      ) : null}
    </SidebarGroup>
  );
}
